import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useLang } from "../../context/LanguageContext";
import {
  defaultCornersFullFrame,
  isConvexQuadOrdered,
  type Point,
  warpImageFileWithQuad,
} from "./perspectiveWarp";

export type CornerEditorPassportFooter = "post_capture" | "review_edit";

export interface DocumentCornerEditorProps {
  open: boolean;
  /** Object URL for preview (same page as ``file``). */
  imageUrl: string;
  file: File;
  onClose: () => void;
  /** Called with JPEG straightened page on success. */
  onApply: (newFile: File, options?: { intent: "review" | "add_page" }) => void;
  /** 1-based index shown in the header (PAGE {{n}}). */
  pageNumber?: number;
  /** Passport flow uses retake copy and passport-specific hint text. */
  variant?: "standard" | "passport";
  /**
   * Passport-only footer layouts from design review:
   * - ``post_capture``: Done (review) + Add page (continue shooting).
   * - ``review_edit``: single Done back to review.
   * Omit for standard document flow (single primary button).
   */
  passportFooter?: CornerEditorPassportFooter | null;
  /** When editing from review, Retake opens the camera instead of closing without replace. */
  onPassportRetakeFromReview?: () => void;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function clientPointToNatural(
  clientX: number,
  clientY: number,
  imgRect: DOMRectReadOnly,
  nw: number,
  nh: number,
): Point {
  const nx = ((clientX - imgRect.left) / Math.max(imgRect.width, 1)) * nw;
  const ny = ((clientY - imgRect.top) / Math.max(imgRect.height, 1)) * nh;
  // Allow edges at nw/nh inclusive for corner handles; warp samples clamp safely.
  return { x: clamp(nx, 0, nw), y: clamp(ny, 0, nh) };
}

export default function DocumentCornerEditor({
  open,
  imageUrl,
  file,
  onClose,
  onApply,
  pageNumber: pageNumberProp,
  variant = "standard",
  passportFooter = null,
  onPassportRetakeFromReview,
}: DocumentCornerEditorProps) {
  const { t } = useLang();
  const imgRef = useRef<HTMLImageElement | null>(null);
  /** Element that owns pointer capture - release on that node only (iOS-safe). */
  const pointerCaptureElRef = useRef<HTMLElement | null>(null);
  const orientationLockedRef = useRef(false);

  const [naturalW, setNaturalW] = useState(0);
  const [naturalH, setNaturalH] = useState(0);
  const [corners, setCorners] = useState<Point[] | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetCorners = useCallback(() => {
    if (naturalW <= 0 || naturalH <= 0) return;
    setCorners(defaultCornersFullFrame(naturalW, naturalH));
    setError(null);
  }, [naturalW, naturalH]);

  useEffect(() => {
    if (!open) {
      setNaturalW(0);
      setNaturalH(0);
      setCorners(null);
      setDragIdx(null);
      setBusy(false);
      setError(null);
      return;
    }
    const img = imgRef.current;
    if (!img?.complete || img.naturalWidth <= 0) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    setNaturalW(nw);
    setNaturalH(nh);
    setCorners(defaultCornersFullFrame(nw, nh));
    setError(null);
  }, [open, imageUrl]);

  const handleImgLoad = useCallback(() => {
    if (!open) return;
    const img = imgRef.current;
    if (!img) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return;
    setNaturalW(nw);
    setNaturalH(nh);
    setCorners(defaultCornersFullFrame(nw, nh));
    setError(null);
  }, [open]);

  /** Passport spreads read best in landscape; lock where the platform allows. */
  useEffect(() => {
    if (!open || variant !== "passport") return;
    const o = typeof screen !== "undefined" ? screen.orientation : null;
    if (!o || typeof o.lock !== "function") return;
    let active = true;
    void (async () => {
      try {
        await o.lock("landscape");
        if (!active) {
          try {
            o.unlock?.();
          } catch {
            /* ignore */
          }
          return;
        }
        orientationLockedRef.current = true;
      } catch {
        /* iOS Safari / insecure context: keep portrait; layout still favors wide workspace */
      }
    })();
    return () => {
      active = false;
      if (!orientationLockedRef.current) return;
      orientationLockedRef.current = false;
      try {
        o.unlock?.();
      } catch {
        /* ignore */
      }
    };
  }, [open, variant]);

  useEffect(() => {
    if (!open || dragIdx === null) return;

    const idx = dragIdx;
    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      const img = imgRef.current;
      if (!img || naturalW <= 0 || naturalH <= 0) return;
      const rect = img.getBoundingClientRect();
      const p = clientPointToNatural(ev.clientX, ev.clientY, rect, naturalW, naturalH);
      setCorners((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        next[idx] = p;
        return next;
      });
    };

    const endDrag = (ev: PointerEvent) => {
      ev.preventDefault();
      try {
        pointerCaptureElRef.current?.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      pointerCaptureElRef.current = null;
      setDragIdx(null);
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", endDrag, { passive: false });
    window.addEventListener("pointercancel", endDrag, { passive: false });

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, [open, dragIdx, naturalW, naturalH]);

  const beginDrag = (idx: number) => (ev: ReactPointerEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    const el = ev.currentTarget as HTMLElement;
    pointerCaptureElRef.current = el;
    try {
      el.setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
    setDragIdx(idx);
    setError(null);
  };

  const handleApply = async (intent: "review" | "add_page" = "review") => {
    if (!corners || busy || naturalW <= 0) return;
    if (!isConvexQuadOrdered(corners)) {
      setError(t("documentScanner.cornerEditorInvalidQuad"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await warpImageFileWithQuad(file, corners, {
        maxLongEdge: 2400,
        jpegQuality: 0.92,
      });
      if (passportFooter === "post_capture") {
        onApply(out, { intent });
      } else if (passportFooter === "review_edit") {
        onApply(out, { intent: "review" });
      } else {
        onApply(out);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg === "invalid-quad") setError(t("documentScanner.cornerEditorInvalidQuad"));
      else if (msg === "singular-homography") setError(t("documentScanner.cornerEditorError"));
      else setError(t("documentScanner.cornerEditorError"));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const pageNum = Math.max(1, pageNumberProp ?? 1);
  const pageTitle = t("documentScanner.passportPageTitle").replace("{{n}}", String(pageNum));
  const hint =
    variant === "passport"
      ? t("documentScanner.passportCornerDragHint")
      : t("documentScanner.cornerEditorHint");
  const leaveLabel =
    variant === "passport" ? t("documentScanner.passportCornerRetake") : t("documentScanner.cornerEditorCancel");

  const pct = (x: number, axis: "x" | "y") =>
    axis === "x" ? `${(x / Math.max(naturalW, 1)) * 100}%` : `${(x / Math.max(naturalH, 1)) * 100}%`;

  const passportWorkspace =
    variant === "passport"
      ? {
          /** Fill space between hint and footer; image scales with max-h-full inside this region. */
          card: "w-full max-w-[min(100%,min(96dvw,1100px))] px-4 py-3 flex-1 min-h-0 min-w-0",
          inner: "relative inline-block select-none touch-none max-w-full max-h-full",
          img: "block max-w-full max-h-full h-auto w-auto object-contain rounded-lg pointer-events-none",
        }
      : {
          card: "w-full max-w-[min(100%,520px)] px-5 py-6",
          inner: "max-w-full max-h-[min(52vh,calc(100dvh-280px))]",
          img: "max-w-full max-h-[min(52vh,calc(100dvh-280px))] h-auto w-auto",
        };

  return (
    <div
      className="fixed inset-0 z-[169] flex flex-col bg-black"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gm-corner-editor-page-title"
      style={{ touchAction: "none" }}
    >
      <header className="shrink-0 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-2">
        <div className="flex justify-start min-w-0">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (passportFooter === "review_edit" && typeof onPassportRetakeFromReview === "function") {
                onPassportRetakeFromReview();
              } else {
                onClose();
              }
            }}
            className="min-h-[44px] px-1 py-2 text-[15px] font-semibold text-white hover:text-white/90 disabled:opacity-40 touch-manipulation"
          >
            {leaveLabel}
          </button>
        </div>
        <h2
          id="gm-corner-editor-page-title"
          className="text-[15px] font-bold text-white uppercase tracking-wide text-center whitespace-nowrap px-1"
        >
          {pageTitle}
        </h2>
        <div className="flex justify-end min-w-0">
          <button
            type="button"
            disabled={busy || naturalW <= 0}
            onClick={resetCorners}
            aria-label={t("documentScanner.cornerEditorReset")}
            className="min-h-[44px] px-1 py-2 text-[15px] font-semibold text-white hover:text-white/90 disabled:opacity-40 touch-manipulation"
          >
            {t("documentScanner.cornerEditorResetHeader")}
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain">
        <p
          className={`text-center text-[14px] text-white px-5 shrink-0 leading-snug ${
            variant === "passport" ? "pt-3 pb-2" : "pt-1 pb-4"
          }`}
        >
          {hint}
        </p>

        {/*
          Passport: flex-1 fills viewport between hint and footer so the crop preview scales up.
          Standard: keep centered workspace in remaining height.
        */}
        <div
          className={
            variant === "passport"
              ? "flex flex-1 min-h-0 flex-col px-2 pt-1 pb-2 overflow-hidden"
              : "flex-1 min-h-0 flex items-center justify-center px-2 sm:px-4 pb-2 sm:pb-4 overflow-visible"
          }
        >
          <div
            className={`rounded-2xl bg-zinc-900/95 border border-white/[0.06] shadow-inner flex items-center justify-center ${passportWorkspace.card}`}
          >
            <div className={variant === "passport" ? passportWorkspace.inner : `relative inline-block select-none touch-none ${passportWorkspace.inner}`}>
              <img
                ref={imgRef}
                src={imageUrl}
                alt=""
                draggable={false}
                onLoad={handleImgLoad}
                className={
                  variant === "passport"
                    ? passportWorkspace.img
                    : `block rounded-lg pointer-events-none ${passportWorkspace.img}`
                }
              />

              {corners && naturalW > 0 && naturalH > 0 ? (
                <>
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none overflow-visible z-[1]"
                    viewBox={`0 0 ${naturalW} ${naturalH}`}
                    preserveAspectRatio="xMidYMid meet"
                    aria-hidden="true"
                  >
                    <polygon
                      points={corners.map((c) => `${c.x},${c.y}`).join(" ")}
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth={2}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      vectorEffect="nonScalingStroke"
                    />
                  </svg>

                  {corners.map((c, idx) => (
                    <button
                      key={idx}
                      type="button"
                      aria-label={`${t("documentScanner.cornerHandleAria")} ${idx + 1}`}
                      disabled={busy}
                      onPointerDown={beginDrag(idx)}
                      className="absolute z-[2] min-w-[44px] min-h-[44px] w-11 h-11 -ml-[22px] -mt-[22px] flex items-center justify-center rounded-full border-0 bg-transparent disabled:opacity-40 touch-manipulation cursor-grab active:cursor-grabbing p-0"
                      style={{ left: pct(c.x, "x"), top: pct(c.y, "y") }}
                    >
                      <span
                        className="pointer-events-none block h-[22px] w-[22px] shrink-0 rounded-full border-[2px] border-white bg-transparent shadow-[0_0_0_0.5px_rgba(0,0,0,0.35),0_1px_4px_rgba(0,0,0,0.3)]"
                        aria-hidden
                      />
                    </button>
                  ))}
                </>
              ) : null}
            </div>
          </div>
        </div>

        {error ? (
          <p className="text-center text-[13px] text-red-300 px-4 shrink-0 pb-2" role="alert">
            {error}
          </p>
        ) : null}

        <div
          className={`shrink-0 px-4 pb-[max(20px,env(safe-area-inset-bottom))] bg-black ${
            variant === "passport" ? "pt-4" : "pt-2"
          }`}
        >
          {passportFooter === "post_capture" ? (
            <div className="flex gap-3">
              <button
                type="button"
                disabled={busy || naturalW <= 0}
                onClick={() => void handleApply("review")}
                className="flex-1 min-h-[54px] rounded-full border-2 border-white bg-black text-white text-[16px] font-bold disabled:opacity-45 flex items-center justify-center gap-2 touch-manipulation"
              >
                {busy ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl shrink-0" aria-hidden="true" />
                    {t("documentScanner.cornerEditorBusy")}
                  </>
                ) : (
                  t("documentScanner.cornerEditorDone")
                )}
              </button>
              <button
                type="button"
                disabled={busy || naturalW <= 0}
                onClick={() => void handleApply("add_page")}
                className="flex-1 min-h-[54px] rounded-full bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white text-[16px] font-bold disabled:opacity-45 flex items-center justify-center gap-2 touch-manipulation shadow-lg shadow-emerald-900/40"
              >
                {t("documentScanner.cornerEditorAddPage")}
              </button>
            </div>
          ) : passportFooter === "review_edit" ? (
            <button
              type="button"
              disabled={busy || naturalW <= 0}
              onClick={() => void handleApply("review")}
              className="w-full min-h-[54px] rounded-full border-2 border-white bg-black text-white text-[16px] font-bold disabled:opacity-45 flex items-center justify-center gap-2 touch-manipulation"
            >
              {busy ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl shrink-0" aria-hidden="true" />
                  {t("documentScanner.cornerEditorBusy")}
                </>
              ) : (
                t("documentScanner.cornerEditorDone")
              )}
            </button>
          ) : (
            <button
              type="button"
              disabled={busy || naturalW <= 0}
              onClick={() => void handleApply("review")}
              className="w-full min-h-[54px] rounded-full bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white text-[16px] font-bold disabled:opacity-45 flex items-center justify-center gap-2 touch-manipulation shadow-lg shadow-emerald-900/40"
            >
              {busy ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl shrink-0" aria-hidden="true" />
                  {t("documentScanner.cornerEditorBusy")}
                </>
              ) : (
                t("documentScanner.cornerEditorApplyContinue")
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
